// components/hauler/PayoutSetupCard.js
// Phase 1 (Hauler Payouts) — "Set up payouts" card for the Hauler Profile tab.
//
// Talks ONLY to the connect-onboarding edge function:
//   { action: "status" }  → live onboarding/readiness state (read on mount and
//                           when the tab regains focus, e.g. returning from
//                           Stripe's hosted onboarding).
//   { action: "onboard" } → creates the Express account if needed and returns a
//                           Stripe-hosted onboarding link; we redirect to it.
//
// No Stripe keys or account state live in the client. Status is never cached —
// the edge function reads it live from the Stripe API each time.
//
// States rendered:
//   loading      → "Checking payout status…"
//   not_started  → explain + [Set up payouts]
//   in_progress  → account exists, requirements still due → [Continue setup]
//   ready        → green "Payouts ready" row (readyToReceivePayments === true)
//   error        → soft message + [Try again]

import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../../services/supabaseService";

const COLORS = {
  text: "var(--hl-text)",
  sub: "var(--hl-text-sub)",
  muted: "var(--hl-text-muted)",
  card: "var(--hl-card)",
  border: "var(--hl-border)",
  green: "var(--hl-green)",
  sky: "var(--hl-sky)",
  red: "var(--hl-red)",
};

async function callConnectOnboarding(action) {
  const { data: { session: activeSession } } = await supabase.auth.getSession();
  if (!activeSession?.access_token) {
    throw new Error("Your session expired. Sign in again to manage payouts.");
  }
  // return_origin: where Stripe should send the Hauler back to after hosted
  // onboarding. Without it the server falls back to production — which
  // silently teleported preview testers onto the live site mid-flow. The
  // server validates this against an allowlist of our own origins.
  const returnOrigin = typeof window !== "undefined" ? window.location.origin : null;
  const { data, error } = await supabase.functions.invoke("connect-onboarding", {
    body: { action, return_origin: returnOrigin },
    headers: { Authorization: `Bearer ${activeSession.access_token}` },
  });
  if (error || data?.error) {
    throw new Error(data?.error || error?.message || "Payout service unavailable");
  }
  return data;
}

export default function PayoutSetupCard({ session }) {
  const [phase, setPhase] = useState("loading"); // loading | not_started | in_progress | ready | error
  const [busy, setBusy] = useState(false);       // an onboard redirect is in flight

  const refreshStatus = useCallback(async () => {
    if (!session?.user?.id) { setPhase("error"); return; }
    try {
      const data = await callConnectOnboarding("status");
      if (data.status === "not_started") setPhase("not_started");
      else if (data.readyToReceivePayments) setPhase("ready");
      else setPhase("in_progress");
    } catch (e) {
      console.warn("[PayoutSetupCard] status check failed:", e?.message);
      setPhase("error");
    }
  }, [session?.user?.id]);

  useEffect(() => {
    setPhase("loading");
    refreshStatus();
    // Re-check when the tab regains focus — this is how the card updates after
    // the Hauler returns from Stripe's hosted onboarding flow.
    const onVisible = () => { if (!document.hidden) refreshStatus(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshStatus]);

  async function startOnboarding() {
    if (busy) return;
    setBusy(true);
    try {
      const data = await callConnectOnboarding("onboard");
      if (data?.url) {
        // Hand off to Stripe's hosted onboarding. Same-tab redirect: Stripe
        // returns the Hauler to the app via return_url when they finish.
        window.location.href = data.url;
        return; // keep busy=true; we're navigating away
      }
      throw new Error("No onboarding link returned");
    } catch (e) {
      console.warn("[PayoutSetupCard] onboarding start failed:", e?.message);
      setPhase("error");
      setBusy(false);
    }
  }

  return (
    <div style={{
      background: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 14,
      padding: "14px 16px",
      marginBottom: 14,
    }}>
      <div style={{
        fontSize: 11, letterSpacing: 1.5, fontWeight: 700,
        color: COLORS.sub, textTransform: "uppercase",
      }}>
        Payouts
      </div>

      {phase === "loading" && (
        <div style={{ fontSize: 12, color: COLORS.sub, marginTop: 8 }}>
          Checking payout status…
        </div>
      )}

      {phase === "ready" && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}>
          <span aria-hidden="true" style={{
            width: 12, height: 12, borderRadius: "50%",
            background: COLORS.green, boxShadow: "0 0 8px rgba(3,166,106,.6)", flexShrink: 0,
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>Payouts ready</div>
            <div style={{ fontSize: 12, color: COLORS.sub, marginTop: 2, lineHeight: 1.4 }}>
              Your bank details are connected. Job earnings pay out to your account.
            </div>
          </div>
        </div>
      )}

      {(phase === "not_started" || phase === "in_progress") && (
        <>
          <div style={{ fontSize: 12, color: COLORS.sub, marginTop: 8, lineHeight: 1.5 }}>
            {phase === "not_started"
              ? "Connect your bank or debit card to get paid for completed jobs. Setup is handled securely by Stripe and takes about two minutes."
              : "Your payout setup isn't finished yet. Pick up where you left off — it only takes a minute."}
          </div>
          <button
            type="button"
            onClick={startOnboarding}
            disabled={busy}
            style={{
              width: "100%",
              marginTop: 12,
              minHeight: 44,
              borderRadius: 12,
              border: "none",
              background: busy ? "rgba(61,144,217,.5)" : COLORS.sky,
              color: "#FFFFFF",
              fontFamily: "inherit",
              fontSize: 14,
              fontWeight: 800,
              cursor: busy ? "wait" : "pointer",
            }}
          >
            {busy ? "Opening secure setup…" : (phase === "not_started" ? "Set up payouts" : "Continue payout setup")}
          </button>
        </>
      )}

      {phase === "error" && (
        <>
          <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 8, lineHeight: 1.5 }}>
            Payout status is unavailable right now.
          </div>
          <button
            type="button"
            onClick={() => { setPhase("loading"); refreshStatus(); }}
            style={{
              width: "100%",
              marginTop: 10,
              minHeight: 44,
              borderRadius: 12,
              border: `1px solid ${COLORS.border}`,
              background: "transparent",
              color: COLORS.text,
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </>
      )}
    </div>
  );
}
